import { createContext } from 'react';
import type { User } from '@/types/api';

export type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

/**
 * Signing in no longer always produces a session. An administrator gets a challenge
 * that must be answered with an authenticator code first.
 */
export type SignInResult =
  | { kind: 'session'; user: User }
  | { kind: 'mfa'; challengeToken: string; enrollmentRequired: boolean };

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  /** True once the token is close enough to expiry that we should ask for a re-login. */
  expiringSoon: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  /** Finish a login once a second factor (or a reset) has produced a token. */
  adoptToken: (token: string) => Promise<User>;
  signOut: () => void;
  /** Swap in a fresh token without tearing down the page the user is on. */
  refreshSession: (email: string, password: string) => Promise<void>;
  setUser: (user: User) => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
